import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useContextSelector } from "use-context-selector";
import { StateContext } from "../../contexts/StateContext";
import { deleteGoldChit, goldChitOnboardByDateRange, joinGoldChit } from "../../api/PupilApi";
import { useNavigate } from "react-router-dom";
import Category from "../../components/pageProps/shopPage/shopBy/Category";

const UserGoldChit = () => {
  const navigate = useNavigate();
  const role = useContextSelector(StateContext, (state) => state.role);

  const userEmail = useContextSelector(StateContext, (state) => {
    return (
      state.user?.email ||
      state.email ||
      state.pupilEmail ||
      state.userEmail ||
      null
    );
  });

  const [chits, setChits] = useState([]);
  const [selectedChit, setSelectedChit] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAdmin = role === "ADMIN";

  const generalRulesList = [
    "Installments must be paid every month between the 1st and 5th.",
    "Upon completing 12 installments for a gold scheme, the specified benefit will be provided within 30 days.",
    "If a member discontinues a gold scheme, the total paid amount will be refunded after deducting one month's installment as a membership fee.",
    "Failure to pay two consecutive installments will result in disqualification from the scheme as per company policy.",
    "All gold provided is certified 916 KDM hallmark quality.",
  ];

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startDate = new Date(`${currentYear}-01-01`);
    const endDate = new Date(`${currentYear}-12-31`);
    const formatDate = (date) => date.toISOString().split("T")[0];

    goldChitOnboardByDateRange("goldChit", formatDate(startDate), formatDate(endDate))
      .then((res) => {
        const goldChits =
          (res?.data || [])
            .filter((item) => item.goldChitOnboard)
            .map((item, index) => ({
              ...item.goldChitOnboard,
              pupilEmail: item.pupilEmail || null,
              chitId: index + 1,
            })) || [];

        setChits(goldChits);
        toast.success("Fetched Gold Chit Data Successfully!");
      })
      .catch((err) => {
        toast.error("Error fetching gold chit data: " + (err?.message || err));
      });
  }, []);

  const handleViewDetails = (chit) => {
    setSelectedChit(chit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedChit(null);
  };

  const handleJoinChit = async (chit) => {
    const storageEmail =
      localStorage.getItem("email") ||
      localStorage.getItem("pupilEmail") ||
      localStorage.getItem("userEmail") ||
      null;
    const token = localStorage.getItem("token") || localStorage.getItem("authToken") || null;

    const isSignedIn = Boolean(userEmail) || Boolean(storageEmail) || Boolean(role) || Boolean(token);

    if (!isSignedIn) {
      toast.info("Please sign up or log in to join this chit.");
      navigate("/signup");
      return;
    }

    if (!window.confirm("Are you sure you want to join this chit?")) return;

    try {
      await joinGoldChit({
        chitTitle: chit.chitTitle,
        category: chit.Category,
        goldGram: chit.goldGram,
        numMonths: chit.numMonths,
        goldRate: chit.goldRate,
        startDate: chit.startDate,
        endDate: chit.endDate,
        location: chit.location,
        monthlyInstallment: chit.monthlyInstallment,
        monthlyGoldContribution: chit.monthlyGoldContribution,
        chitPlanData: chit.chitPlanData || [],
        totalPaid: chit.totalPaid || 0,
        totalGoldCollected: chit.totalGoldCollected || 0,
        chitPupilEmail: userEmail || storageEmail || chit.pupilEmail || null,
      });

      toast.success("Successfully joined this chit!");
      handleCloseModal();

      setChits((prev) =>
        prev.map((c) => (c.chitId === chit.chitId ? { ...c, joined: true } : c))
      );

      window.close();
      navigate("/myprofile");
    } catch (err) {
      toast.error("Failed to join chit: " + (err?.response?.data || err?.message || err));
    }
  };

  const formatDisplayDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-3xl p-8 sm:p-12 shadow-2xl mb-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-black opacity-5"></div>
            <div className="relative">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg">
                Golden Opportunities
              </h1>
              <p className="text-xl text-amber-100 max-w-3xl mx-auto leading-relaxed">
                Secure your future with our trusted Gold Chit plans. Build wealth systematically with the assurance of pure gold.
              </p>
            </div>
          </div>
        </div>

        {/* Available Chits Section */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-4">
              Available Gold Chits
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto rounded-full"></div>
            <p className="text-gray-600 mt-4 text-lg">Choose from our carefully curated gold investment plans</p>
          </div>

          <div className="grid gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {chits?.map((chit) => (
              <div
                key={chit.chitId}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100 flex flex-col transform hover:-translate-y-2"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-amber-400 rounded-full -mr-10 -mt-10 opacity-20"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-amber-300 rounded-full -ml-8 -mb-8 opacity-20"></div>
                  <div className="relative">
                    <h3 className="text-lg font-bold truncate mb-1">{chit.chitTitle}</h3>
                    <div className="flex items-center justify-center space-x-2">
                      <span className="bg-amber-600 px-2 py-1 rounded-full text-xs font-semibold">
                        {chit.goldGram} Grams
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Date Section */}
                  <div className="flex justify-between mb-6">
                    <div className="text-center">
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-3 rounded-xl border border-amber-100">
                        <p className="text-xs text-gray-500 font-medium">Start Date</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {formatDisplayDate(chit.startDate)}
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-3 rounded-xl border border-amber-100">
                        <p className="text-xs text-gray-500 font-medium">End Date</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {formatDisplayDate(chit.endDate)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Installment Highlight */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-4 text-center border border-green-200 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-emerald-400"></div>
                    <p className="text-xs text-gray-600 mb-1">Monthly Installment</p>
                    <p className="text-xl font-bold text-green-700">
                      ₹{chit.monthlyInstallment?.toLocaleString()}
                    </p>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Gold Rate</span>
                      <span className="font-semibold text-amber-700">₹{chit.goldRate?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Duration</span>
                      <span className="font-semibold text-gray-800">{chit.numMonths} Months</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleViewDetails(chit)}
                    className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl mt-auto"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Chit Details Modal */}
        {isModalOpen && selectedChit && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transform animate-scale-in">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-8 rounded-t-3xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400 rounded-full -mr-16 -mt-16 opacity-20"></div>
                <div className="relative flex justify-between items-center">
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">{selectedChit.chitTitle}</h2>
                    <p className="text-amber-100 flex items-center">
                      <span className="bg-amber-600 px-3 py-1 rounded-full text-sm font-semibold mr-2">
                        {selectedChit.goldGram} Grams Gold
                      </span>
                      <span>{selectedChit.numMonths} Months Plan</span>
                    </p>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className="text-white hover:text-amber-200 text-3xl transition-transform duration-200 hover:scale-110 bg-black bg-opacity-20 rounded-full w-10 h-10 flex items-center justify-center"
                  >
                    &times;
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8">
                {/* Key Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-2xl border border-amber-100 text-center">
                    <div className="text-amber-500 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-1">Start Date</h3>
                    <p className="text-lg font-bold text-gray-800">
                      {formatDisplayDate(selectedChit.startDate)}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-2xl border border-amber-100 text-center">
                    <div className="text-amber-500 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-1">End Date</h3>
                    <p className="text-lg font-bold text-gray-800">
                      {formatDisplayDate(selectedChit.endDate)}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-2xl border border-green-100 text-center">
                    <div className="text-green-500 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-1">Monthly Installment</h3>
                    <p className="text-xl font-bold text-green-700">
                      ₹{selectedChit.monthlyInstallment?.toLocaleString()}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-2xl border border-blue-100 text-center">
                    <div className="text-blue-500 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-700 mb-1">Duration</h3>
                    <p className="text-lg font-bold text-blue-700">{selectedChit.numMonths} Months</p>
                  </div>
                </div>

                {/* Chit Plan Table */}
                {selectedChit.chitPlanData && selectedChit.chitPlanData.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Payment Schedule
                    </h3>
                    <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-200">
                      <table className="min-w-full bg-white">
                        <thead>
                          <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                            <th className="py-4 px-6 border-b text-left font-semibold text-gray-700">Month</th>
                            <th className="py-4 px-6 border-b text-center font-semibold text-gray-700">Installment</th>
                            <th className="py-4 px-6 border-b text-center font-semibold text-gray-700">Total Paid</th>
                            <th className="py-4 px-6 border-b text-center font-semibold text-gray-700">Gold Collected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedChit.chitPlanData.map((data, index) => (
                            <tr
                              key={index}
                              className={index % 2 === 0 ? "bg-white" : "bg-gray-50 hover:bg-gray-100 transition-colors"}
                            >
                              <td className="py-4 px-6 border-b text-left font-medium text-gray-800">
                                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">
                                  Month {data.month || index + 1}
                                </span>
                              </td>
                              <td className="py-4 px-6 border-b text-center font-semibold text-green-600">
                                ₹{data.monthlyInstallment?.toLocaleString() || "-"}
                              </td>
                              <td className="py-4 px-6 border-b text-center font-semibold text-blue-600">
                                ₹{data.totalPaid?.toLocaleString() || "0"}
                              </td>
                              <td className="py-4 px-6 border-b text-center font-semibold text-amber-600">
                                <span className="bg-amber-50 px-3 py-1 rounded-full">
                                  {data.totalGoldCollected || "0"}g
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-b-3xl border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={handleCloseModal}
                  className="px-8 py-3 bg-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-400 transition-all duration-200 shadow hover:shadow-lg"
                >
                  Close Details
                </button>

                {selectedChit.joined ? (
                  <button
                    className="px-8 py-3 bg-green-500 text-white rounded-xl font-semibold cursor-not-allowed shadow-lg flex items-center"
                    disabled
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Already Joined
                  </button>
                ) : (
                  <button
                    onClick={() => handleJoinChit(selectedChit)}
                    className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                    Join This Chit
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rules & Regulations Section */}
        <section className="mt-20">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="bg-blue-500 p-3 rounded-xl mr-4">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-800">Rules & Regulations</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {generalRulesList.map((rule, index) => (
                <div key={index} className="flex items-start p-4 bg-white rounded-xl shadow-sm border border-blue-100">
                  <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  <p className="text-gray-700 leading-relaxed">{rule}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserGoldChit;